import { BlankContainer } from "../molecules/blank-container";
import { Input } from "../atoms/Input";
import { useEffect, useState } from "react";
import { Button } from "../atoms/button";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../auth/AuthContext";
import {
  validateEmail,
  validatePhoneNumberWithCountryCode,
} from "../../utils/common";
import { AlertIcon } from "../icons/alert-icon";

const backgroundImage = "/images/background/onboard.png";
const paypalImage = "/images/Logo/PayPal.svg";

interface PaymentOnboardProps {
  skippIfDataExists?: boolean;
}

function validatePaymentId(paymentId: string): boolean {
  if (!paymentId) {
    return false;
  }

  if (paymentId.includes("@")) {
    return validateEmail(paymentId);
  }

  return validatePhoneNumberWithCountryCode(paymentId);
}

function getPaymentIdError(paymentId: string): string {
  if (!paymentId) {
    return "Required";
  }

  if (paymentId.includes("@") && !validateEmail(paymentId)) {
    return "Invalid email address";
  }

  return "Invalid phone number. Check your country code. e.g., (+1) 000-000-0000.";
}

export const PaymentOnboard = ({ skippIfDataExists }: PaymentOnboardProps) => {
  const [emailOrPhone, setEmailOrPhone] = useState("");
  const [formDirty, setFormDirty] = useState(false);
  const navigate = useNavigate();
  const { user } = useAuth() as any;
  const [notifyError, setNotifyError] = useState(false);

  const setPaymentInformation = async () => {
    const apiUrl = process.env.REACT_APP_API_URL;
    const getUserUrl = `${apiUrl}/users/${user?.email}/`;
    const response = await fetch(getUserUrl, {
      method: "PATCH",
      headers: {
        Authorization: `bearer ${user?.accessToken}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        payment_contact_id: emailOrPhone,
      }),
    });
    if (!response.ok) {
      throw new Error("Failed to fetch user.");
    }
  };

  useEffect(() => {
    async function verifyUserPaymentInfo() {
      if (user?.email) {
        const apiUrl = process.env.REACT_APP_API_URL;
        const getUserUrl = `${apiUrl}/users/${user.email}/`;
        const response = await fetch(getUserUrl, {
          method: "GET",
          headers: {
            Authorization: `bearer ${user?.accessToken}`,
          },
        });

        if (!response.ok) {
          navigate("/signup");
        }

        const userData = await response.json();
        setEmailOrPhone(userData.payment_contact_id);

        if (userData.is_internal) {
          // Prevent internal to stay on this page
          navigate("/instructions");
        }

        if (userData.payment_contact_id && skippIfDataExists) {
          if (validatePaymentId(userData.payment_contact_id)) {
            return true;
          } else {
            setNotifyError(true);
            setFormDirty(true);
          }
        }
      }
    }
    verifyUserPaymentInfo().then((ok) => ok && navigate("/"));
  }, []);

  return (
    <div
      className="w-full h-screen flex justify-center items-center flex-col"
      style={{
        backgroundImage: `url(${backgroundImage})`,
        backgroundSize: "cover",
        backgroundRepeat: "no-repeat",
        backgroundPositionX: "center",
      }}
    >
      <h1 className="mb-6" style={{ fontSize: "1.4rem" }}>
        Setup your payment
      </h1>
      <BlankContainer showOverflow={true}>
        <div className="flex flex-col items-center space-y-4 h-[70vh] relative">
          {notifyError && (
            <span className="absolute bg-[#E50000]/50 border border-[#E50000] rounded-xl p-2 text-white top-[-3em] flex">
              <AlertIcon size={24} />
              <span className="ml-1">
                Invalid phone number. Check your country code.
              </span>
            </span>
          )}
          <img src={paypalImage} className="mt-12 mb-8" alt="PayPal Logo" />
          <h2>Enter your PayPal account</h2>
          <p className="font-light">
            Enter your PayPal email address or phone number with country code
            now. After a QA process of approximately 7 business days following
            your audio upload, your earnings will be available for withdrawal to
            your PayPal.
          </p>
          <Input
            label="Email or mobile number"
            onChange={setEmailOrPhone}
            value={emailOrPhone}
            error={
              formDirty && !validatePaymentId(emailOrPhone)
                ? getPaymentIdError(emailOrPhone)
                : undefined
            }
            instructions="e.g., abbygin@gmail.com or +11235551234"
          />
          <br />
          <Button
            type="primary"
            onClick={async () => {
              setFormDirty(true);
              if (validatePaymentId(emailOrPhone)) {
                await setPaymentInformation();
                if (typeof fbq === "function") {
                  fbq("track", "AddPaymentInfo");
                }
                navigate("/instructions");
              }
            }}
          >
            Save and Continue
          </Button>
          <a
            onClick={() => navigate("/instructions")}
            className="mt-auto underline"
            style={{ marginTop: "auto" }}
          >
            Skip for now
          </a>
        </div>
      </BlankContainer>
    </div>
  );
};
